import {
  Building2Icon,
  DropletsIcon,
  GlobeIcon,
  GraduationCapIcon,
  LeafIcon,
  RecycleIcon,
  SearchIcon,
  ThermometerIcon,
  TreePineIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  classroom: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Fundamentos de la Gestión Ambiental',
    subtitle: 'La base que tus alumnos necesitan para entender todo lo demás',
    classroom:
      'El primer contacto con la gestión ambiental puede ser el más transformador o el más aburrido. Este tomo convierte un tema técnico en algo comprensible y relevante para tus alumnos desde el primer día.',
    items: [
      'Conceptos esenciales de gestión ambiental',
      'Ambiente y sociedad: relaciones complejas',
      'Sostenibilidad como marco',
      'Principales problemáticas ambientales',
      'Prevención, mitigación y restauración',
      'Principios fundamentales de la gestión',
      'Actores y responsabilidades',
      'Gobernanza ambiental',
      'Toma de decisiones ambientales',
      'Vocabulario esencial',
    ],
    result:
      'Tus alumnos entienden la gestión ambiental como una disciplina práctica y necesaria, no como un conjunto de términos imposibles de memorizar.',
    icon: GlobeIcon,
  },
  {
    number: '2',
    title: 'Recursos Naturales y Gestión Sostenible',
    subtitle: 'Todo lo que necesitan saber sobre el uso responsable de los recursos',
    classroom:
      'Este es uno de los puntos donde más se traban los alumnos. Este tomo te da el material listo para que comprendan cómo gestionar responsablemente los recursos.',
    items: [
      'Recursos renovables y no renovables',
      'Diferencias y características',
      'Uso responsable de recursos',
      'Conservación de ecosistemas',
      'Consumo sostenible',
      'Estrategias de gestión equilibrada',
      'Huella ecológica',
      'Límites planetarios',
      'Producción y consumo responsable',
      'Presión sobre los recursos',
      '12 casos prácticos de gestión de recursos',
    ],
    result: 'Tus alumnos aprenden cómo tomar decisiones que respeten los límites de los recursos naturales.',
    icon: LeafIcon,
  },
  {
    number: '3',
    title: 'Aspectos, Impactos y Riesgos Ambientales',
    subtitle: 'Cómo identificar y responder a los problemas ambientales',
    classroom:
      'Identificar impactos es fundamental pero complejo. Este tomo te da todo lo que necesitas para que tus alumnos comprendan este proceso.',
    items: [
      'Identificación de aspectos ambientales',
      'Análisis de impactos',
      'Evaluación de significancia',
      'Riesgos ambientales',
      'Amenazas y vulnerabilidades',
      'Prevención de impactos',
      'Priorización de problemas',
      'Toma de decisiones ambiental',
      'Análisis de consecuencias',
      'Alternativas de respuesta',
      '15 casos prácticos de análisis ambiental',
    ],
    result:
      'Tus alumnos desarrollan capacidad para identificar, analizar y responder a problemáticas ambientales reales.',
    icon: SearchIcon,
  },
  {
    number: '4',
    title: 'Gestión del Agua, Suelo y Contaminación',
    subtitle: 'Recursos críticos y estrategias de protección',
    classroom:
      'El agua y el suelo son fundamentales. Este tomo conecta la comprensión científica con la gestión práctica.',
    items: [
      'Uso y gestión del agua',
      'Contaminación del agua',
      'Conservación del suelo',
      'Degradación de suelos',
      'Fuentes de contaminación',
      'Consecuencias ambientales',
      'Estrategias de prevención',
      'Tecnologías de mejora',
      'Monitoreo y control',
      'Regulación y normas',
      '15 casos prácticos de gestión de agua y suelo',
    ],
    result: 'Tus alumnos entienden la importancia de estos recursos y aprenden estrategias para su protección.',
    icon: DropletsIcon,
  },
  {
    number: '5',
    title: 'Gestión de Residuos y Economía Circular',
    subtitle: 'De la disposición lineal a la reutilización sistemática',
    classroom:
      'Los residuos son un desafío global. Este tomo transforma la comprensión de residuos desde un problema a una oportunidad.',
    items: [
      'Generación y clasificación de residuos',
      'Impactos ambientales de los residuos',
      'Prevención y reducción',
      'Reutilización y reparación',
      'Reciclaje y recuperación',
      'Gestión responsable',
      'Economía circular: principios',
      'Diseño circular',
      'Responsabilidad compartida',
      'Cambio de comportamientos',
      '15 casos prácticos de gestión circular',
    ],
    result: 'Tus alumnos comprenden la transición desde modelos lineales a sistemas circulares.',
    icon: RecycleIcon,
  },
  {
    number: '6',
    title: 'Biodiversidad, Ecosistemas y Conservación',
    subtitle: 'El funcionamiento de la naturaleza y su protección',
    classroom:
      'La biodiversidad es fascinante pero amenazada. Este tomo conecta ecología con conservación práctica.',
    items: [
      'Funcionamiento de los ecosistemas',
      'Biodiversidad: tipos y distribución',
      'Servicios ecosistémicos',
      'Amenazas a la biodiversidad',
      'Especies en riesgo',
      'Conservación de ecosistemas',
      'Áreas protegidas',
      'Restauración ambiental',
      'Relación sociedad-naturaleza',
      'Coexistencia sostenible',
      '15 casos prácticos de conservación',
    ],
    result: 'Tus alumnos comprenden por qué la biodiversidad importa y cómo participar en su conservación.',
    icon: TreePineIcon,
  },
  {
    number: '7',
    title: 'Cambio Climático y Desafíos Ambientales',
    subtitle: 'Los mayores desafíos ambientales actuales y futuras respuestas',
    classroom:
      'El cambio climático es urgente pero complejo. Este tomo lo hace comprensible y orientado a la acción.',
    items: [
      'Cambio climático: causas y consecuencias',
      'Vulnerabilidad y adaptación',
      'Resiliencia ambiental',
      'Gases de efecto invernadero',
      'Principales problemáticas ambientales',
      'Contaminación atmosférica',
      'Desertificación',
      'Pérdida de biodiversidad',
      'Respuestas y soluciones',
      'Acción individual y colectiva',
      '15 casos prácticos de desafíos ambientales',
    ],
    result: 'Tus alumnos entienden la urgencia ambiental y reconocen caminos hacia soluciones.',
    icon: ThermometerIcon,
  },
  {
    number: '8',
    title: 'Sistemas de Gestión Ambiental y Mejora Continua',
    subtitle: 'Cómo implementar gestión ambiental en organizaciones',
    classroom:
      'Los sistemas de gestión concretan la responsabilidad. Este tomo muestra cómo las instituciones actúan.',
    items: [
      'Sistemas de Gestión Ambiental',
      'Diagnóstico ambiental inicial',
      'Política y compromisos',
      'Objetivos y metas',
      'Planificación de acciones',
      'Responsabilidades y recursos',
      'Procedimientos operacionales',
      'Indicadores ambientales',
      'Seguimiento y evaluación',
      'Mejora continua',
      'Comunicación y transparencia',
      '12 casos prácticos de sistemas de gestión',
    ],
    result: 'Tus alumnos comprenden cómo la gestión ambiental se operacionaliza en organizaciones reales.',
    icon: Building2Icon,
  },
  {
    number: '9',
    title: 'Enseñanza de la Gestión Ambiental: Planificación, Actividades y Proyectos 2026',
    subtitle: 'Tu guía completa como docente',
    classroom: 'Este tomo te da exactamente lo que necesitas para planificar, organizar y evaluar todo el año.',
    items: [
      'Cómo enseñar gestión ambiental',
      'Objetivos y resultados de aprendizaje',
      'Organización de contenidos',
      'Planificación de clases',
      'Secuencias didácticas',
      'Actividades individuales y grupales',
      'Aprendizaje basado en problemas',
      'Casos y análisis ambientales',
      'Proyectos ambientales',
      'Enseñanza de conceptos fundamentales',
      'Enseñanza de recursos y conservación',
      'Enseñanza de residuos y circularidad',
      'Enseñanza de impactos y riesgos',
      'Enseñanza de sistemas de gestión',
      'Evaluación, rúbricas y criterios',
      'Adaptación a diferentes contextos',
      'Proyecto docente integrador',
      '10 proyectos integradores listos para adaptar',
    ],
    result: 'Nunca más te preguntas “¿qué doy hoy?”. Todo está planificado y contextualizado.',
    icon: GraduationCapIcon,
  },
];
